import React from "react";

const safeHref = (url) => {
  const trimmed = (url || "").trim();
  if (/^(javascript|data|vbscript):/i.test(trimmed)) return "#";
  return trimmed;
};

const INLINE_TOKEN =
  /(`[^`]+`|\*\*[^*]+\*\*|\*[^*\s][^*]*\*|_[^_\s][^_]*_|\[[^\]]+\]\([^)]+\))/;

const parseInline = (text) => {
  const nodes = [];
  let remaining = text;
  let k = 0;
  while (remaining) {
    const match = remaining.match(INLINE_TOKEN);
    if (!match) {
      nodes.push(remaining);
      break;
    }
    if (match.index > 0) nodes.push(remaining.slice(0, match.index));
    const token = match[0];
    const key = `t${(k += 1)}`;
    if (token.startsWith("**")) {
      nodes.push(<strong key={key}>{parseInline(token.slice(2, -2))}</strong>);
    } else if (token.startsWith("`")) {
      nodes.push(
        <code
          key={key}
          className="px-1 py-0.5 bg-gray-100 rounded text-[0.9em] font-mono"
        >
          {token.slice(1, -1)}
        </code>
      );
    } else if (token.startsWith("[")) {
      const m = token.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (m) {
        nodes.push(
          <a
            key={key}
            href={safeHref(m[2])}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline"
          >
            {parseInline(m[1])}
          </a>
        );
      } else {
        nodes.push(token);
      }
    } else {
      nodes.push(<em key={key}>{parseInline(token.slice(1, -1))}</em>);
    }
    remaining = remaining.slice(match.index + token.length);
  }
  return nodes;
};

const HEADING_RE = /^(#{1,3})\s+(.*)$/;
const UL_RE = /^[-*]\s+(.*)$/;
const OL_RE = /^\d+\.\s+(.*)$/;
const HR_RE = /^-{3,}$/;

const isBlockLine = (line) =>
  HEADING_RE.test(line) ||
  UL_RE.test(line) ||
  OL_RE.test(line) ||
  HR_RE.test(line.trim());

const renderHeading = (line, key) => {
  const m = line.match(HEADING_RE);
  const level = m[1].length;
  const content = parseInline(m[2]);
  if (level === 1) {
    return (
      <h1 key={key} className="text-2xl font-bold mt-4 mb-2 text-gray-900">
        {content}
      </h1>
    );
  }
  if (level === 2) {
    return (
      <h2 key={key} className="text-xl font-bold mt-3 mb-2 text-gray-900">
        {content}
      </h2>
    );
  }
  return (
    <h3 key={key} className="text-base font-semibold mt-3 mb-1 text-gray-900">
      {content}
    </h3>
  );
};

const renderList = (items, ordered, key) => {
  const Tag = ordered ? "ol" : "ul";
  const cls = ordered
    ? "list-decimal pl-6 my-2 space-y-1"
    : "list-disc pl-6 my-2 space-y-1";
  return (
    <Tag key={key} className={cls}>
      {items.map((content, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={idx}>{content}</li>
      ))}
    </Tag>
  );
};

export const renderMarkdown = (text) => {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  let k = 0;

  while (i < lines.length) {
    const line = lines[i];
    k += 1;
    const key = `b${k}`;

    if (line.trim() === "") {
      i += 1;
    } else if (HR_RE.test(line.trim())) {
      blocks.push(<hr key={key} className="my-4 border-gray-200" />);
      i += 1;
    } else if (HEADING_RE.test(line)) {
      blocks.push(renderHeading(line, key));
      i += 1;
    } else if (UL_RE.test(line)) {
      const items = [];
      while (i < lines.length && UL_RE.test(lines[i])) {
        items.push(parseInline(lines[i].match(UL_RE)[1]));
        i += 1;
      }
      blocks.push(renderList(items, false, key));
    } else if (OL_RE.test(line)) {
      const items = [];
      while (i < lines.length && OL_RE.test(lines[i])) {
        items.push(parseInline(lines[i].match(OL_RE)[1]));
        i += 1;
      }
      blocks.push(renderList(items, true, key));
    } else {
      const paragraph = [];
      while (
        i < lines.length &&
        lines[i].trim() !== "" &&
        !isBlockLine(lines[i])
      ) {
        paragraph.push(lines[i]);
        i += 1;
      }
      const pNodes = [];
      paragraph.forEach((pline, idx) => {
        if (idx > 0) {
          // eslint-disable-next-line react/no-array-index-key
          pNodes.push(<br key={`br${idx}`} />);
        }
        pNodes.push(
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={`f${idx}`}>{parseInline(pline)}</React.Fragment>
        );
      });
      blocks.push(
        <p key={key} className="my-2 text-gray-800 leading-relaxed">
          {pNodes}
        </p>
      );
    }
  }

  return blocks;
};
